import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, Download, Info } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import InfoModal from "../components/InfoModal";

// Helper function to group entries by quarter
const groupByQuarter = (entries) => {
  const quarters = {};
  entries.forEach(entry => {
    const date = new Date(entry.date);
    const year = date.getFullYear();
    const quarter = Math.floor((date.getMonth()) / 3) + 1;
    const key = `${year}-Q${quarter}`;
    
    if (!quarters[key]) {
      quarters[key] = [];
    }
    quarters[key].push(entry);
  });
  
  // Sort quarters descending (newest first)
  return Object.keys(quarters)
    .sort((a, b) => b.localeCompare(a))
    .map(key => ({
      quarter: key,
      entries: quarters[key].sort((a, b) => new Date(b.date) - new Date(a.date))
    }));
};

export default function Vorfuehrartikel() {
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    article: "",
    percent_used: "",
    grund: "",
    ek_price: ""
  });
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    loadEntries();
    loadProducts();
    loadStock();
  }, []);

  const loadStock = async () => {
    try {
      // Only load geschäftlich stock (demos are always business-related)
      const response = await axios.get(`${API}/stock?type=${encodeURIComponent('geschäftlich')}`);
      setStock(response.data);
    } catch (error) {
      console.error("Fehler beim Laden des Bestands");
    }
  };

  const loadEntries = async () => {
    try {
      const response = await axios.get(`${API}/demos`);
      setEntries(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Produkte");
    }
  };

  const handleProductSelect = (productName) => {
    const product = products.find(p => p.name === productName);
    if (product) {
      setSelectedProduct(product);
      setFormData({
        ...formData,
        article: product.name,
        ek_price: product.tp.toString() || product.kunde.toString() || "0"
      });
    }
  };

  const getTotalPrice = () => {
    const percent = parseFloat(formData.percent_used) || 0;
    const price = parseFloat(formData.ek_price) || 0;
    return (percent / 100) * price;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/demos`, {
        ...formData,
        percent_used: parseFloat(formData.percent_used),
        ek_price: parseFloat(formData.ek_price)
      });
      
      toast.success("Vorführartikel erfasst");
      setFormData({
        date: new Date().toISOString().split('T')[0],
        article: "",
        percent_used: "",
        grund: "",
        ek_price: ""
      });
      setSelectedProduct(null);
      setShowForm(false);
      loadEntries();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/demos/${id}`);
      toast.success("Eintrag gelöscht");
      loadEntries();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const requestDownloadPDF = (quarter) => {
    setPendingDownload(quarter);
    setShowDownloadWarning(true);
  };

  const confirmDownload = async () => {
    if (!pendingDownload) return;
    const quarter = pendingDownload;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/demos/export/${quarter}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Vorfuehrartikel_${quarter}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF wird heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Herunterladen");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Link to="/dashboard">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zu Inventory
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Vorführartikel</h1>
                <p className="text-slate-500 mt-1 text-sm sm:text-base">Demo-Artikel prozentual erfassen (bei 100% wird 1 Stück vom Bestand abgezogen)</p>
              </div>
              <Button 
                onClick={() => setShowInfoModal(true)}
                variant="outline"
                size="sm"
                className="bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-200 w-full sm:w-auto flex-shrink-0"
              >
                <Info className="w-4 h-4 mr-2" />
                Kategorie-Hilfe
              </Button>
            </div>
            <Button 
              onClick={() => setShowForm(!showForm)}
              className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Demo hinzufügen
            </Button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
              <h3 className="text-lg font-semibold mb-4">Neuer Vorführartikel</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="date">Datum *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="article">Artikel (nur Bestand) *</Label>
                  <Select onValueChange={handleProductSelect} value={formData.article}>
                    <SelectTrigger>
                      <SelectValue placeholder="Artikel wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {products
                        .filter(product => {
                          const stockItem = stock.find(s => s.article === product.name);
                          return stockItem && stockItem.stock > 0;
                        })
                        .map((product) => {
                          const stockItem = stock.find(s => s.article === product.name);
                          return (
                            <SelectItem key={product.id} value={product.name}>
                              {product.name} (Bestand: {stockItem?.stock || 0})
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="percent_used">Prozent verwendet *</Label>
                  <Input
                    id="percent_used"
                    type="number"
                    step="0.01"
                    max="100"
                    value={formData.percent_used}
                    onChange={(e) => setFormData({...formData, percent_used: e.target.value})}
                    required
                    placeholder="z.B. 30"
                  />
                  <p className="text-xs text-slate-500 mt-1">Bei 100% wird 1 Stück abgezogen</p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="grund">Grund/Zweck *</Label>
                  <Input
                    id="grund"
                    value={formData.grund}
                    onChange={(e) => setFormData({...formData, grund: e.target.value})}
                    required
                    placeholder="z.B. Kundenvorführung"
                  />
                </div>
                <div>
                  <Label htmlFor="ek_price">EK-Preis *</Label>
                  <Input
                    id="ek_price"
                    type="number"
                    step="0.01"
                    value={formData.ek_price}
                    onChange={(e) => setFormData({...formData, ek_price: e.target.value})}
                    required
                    placeholder="79.92"
                  />
                </div>
                <div className="md:col-span-3">
                  <Label>Anteiliger Wert</Label>
                  <div className="text-xl font-bold text-indigo-600 mt-2">
                    {getTotalPrice().toFixed(2)} €
                    <span className="text-sm text-slate-500 ml-2">
                      ({formData.percent_used || 0}% von {formData.ek_price || 0} €)
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                  Speichern
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowForm(false)}
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          )}

          <div className="space-y-8">
            {groupByQuarter(entries).length === 0 && (
              <div className="text-center py-12 text-slate-500">
                Noch keine Vorführartikel erfasst
              </div>
            )}
            
            {groupByQuarter(entries).map((quarterGroup) => (
              <div key={quarterGroup.quarter} className="bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex justify-between items-center p-4 bg-slate-100 rounded-t-lg border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800">
                    {quarterGroup.quarter.replace('Q', '. Quartal ')}
                  </h3>
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => requestDownloadPDF(quarterGroup.quarter)}
                    className="bg-white hover:bg-slate-50"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF Export
                  </Button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Datum</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Artikel</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Grund</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Prozent</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">EK-Preis</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Anteiliger Wert</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarterGroup.entries.map((entry) => (
                        <tr key={entry.id} className="border-b border-slate-100 hover:bg-white">
                          <td className="py-3 px-4">{new Date(entry.date).toLocaleDateString('de-DE')}</td>
                          <td className="py-3 px-4">{entry.article}</td>
                          <td className="py-3 px-4 text-slate-600">{entry.grund}</td>
                          <td className="py-3 px-4 text-right">
                            <span className="inline-block px-2 py-1 rounded bg-indigo-100 text-indigo-700 font-semibold">
                              {entry.percent_used}%
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">{entry.ek_price.toFixed(2)} €</td>
                          <td className="py-3 px-4 text-right font-semibold">{entry.ek_total.toFixed(2)} €</td>
                          <td className="py-3 px-4 text-right">
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleDelete(entry.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-b-lg border-t border-slate-200">
                  <div className="text-sm text-slate-600">
                    <strong>Quartal Zusammenfassung:</strong> {quarterGroup.entries.length} Vorführartikel, 
                    Gesamtwert: <span className="font-semibold text-indigo-600">
                      {quarterGroup.entries.reduce((sum, entry) => sum + entry.ek_total, 0).toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <InfoModal 
          isOpen={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          category="muster"
        />
      </div>
    </div>
  );
}